import React from "react";
import {useNavigate, useParams} from "react-router-dom";

import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import Checkbox from "@components/inputs/Checkbox";
import {NavBar} from "@components/navigation/NavBar/NavBar";
import {WarningBox} from "@components/cards/messageBox";
import {Country} from "@utils/constants/localisation";
import {convertMinorToMajorCurrency, countrySpecificCurrency} from "@utils/misc/functions";
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";

import {PriceChangeAction, PriceChangeState, calculateNewWeeklyFee} from "./priceChangeReducer";

type Props = {
    navigateTarget: string;
    priceChangeState: PriceChangeState;
    priceChangeDispatch: React.Dispatch<PriceChangeAction>;
};

export default function PriceChangeConfirmation({
                                                    navigateTarget,
                                                    priceChangeState,
                                                    priceChangeDispatch,
                                                }: Props) {
    const {country} = useParams() as { country: Country };
    const navigate = useNavigate();

    const componentName = "Price change confirmation";
    const currency = countrySpecificCurrency(country);

    const newWeeklyFee = calculateNewWeeklyFee(priceChangeState);
    // Zoho's Rental_Fee is already net of any dynamic pricing discount - onboarding writes the
    // adjusted fee there and records the discount separately. So it compares directly against the
    // new fee, which calculateNewWeeklyFee has taken the new coupon off.
    const oldWeeklyFee = priceChangeState.oldWeeklyFee;

    // State is minor currency - convert for display
    const formatFee = (fee: number | null) =>
        fee != null ? `${currency}${convertMinorToMajorCurrency(fee).toFixed(2)}` : "Unknown";

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        navigate(navigateTarget);
    };

    const rowStyle: React.CSSProperties = {display: "flex", flexDirection: "row", width: "100%"};
    const labelStyle: React.CSSProperties = {flex: 1, padding: "10px", boxSizing: "border-box", fontWeight: "bold"};
    const valueStyle: React.CSSProperties = {flex: 1, padding: "10px", boxSizing: "border-box", textAlign: "right"};

    return (
        <>
            <NavBar pageTitle="Confirm new price" backAction={undefined}/>
            <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}></div>
            <WarningBox
                message={
                    <>
                        <b>Chargebee will not be updated automatically.</b> This exchange changes the price in
                        Zoho and on the customer's contract only. The Chargebee subscription must be amended
                        separately.
                        <br/><br/>
                        Finance should already have been notified of this price change via Teams.
                    </>
                }
                testId="chargebee-warning"
            />
            <form style={{margin: "2.5vh 2.5vw"}} onSubmit={handleSubmit}>
                <div style={{margin: "2.5vh 0vw"}}>
                    <div style={rowStyle}>
                        <div style={labelStyle}>Current weekly fee</div>
                        <div style={valueStyle} data-testid="old-weekly-fee">{formatFee(oldWeeklyFee)}</div>
                    </div>
                    <div style={rowStyle}>
                        <div style={labelStyle}>New weekly fee</div>
                        <div style={valueStyle} data-testid="new-weekly-fee">{formatFee(newWeeklyFee)}</div>
                    </div>
                    <div style={rowStyle}>
                        <div style={labelStyle}>New plan</div>
                        <div style={valueStyle}>{priceChangeState.newPlanName || "Unknown"}</div>
                    </div>
                    <div style={rowStyle}>
                        <div style={labelStyle}>Dynamic pricing discount</div>
                        <div style={valueStyle}>
                            {priceChangeState.couponId
                                ? `${priceChangeState.couponId} (${formatFee(priceChangeState.couponDiscount)})`
                                : "None"}
                        </div>
                    </div>
                </div>

                <div style={{margin: "2.5vh 0vw"}}>
                    <Checkbox
                        id="priceChangeConfirmed"
                        name="priceChangeConfirmed"
                        label=" I confirm the new weekly price above is correct and finance have been notified"
                        required
                        checked={priceChangeState.priceConfirmed}
                        onChange={(checked: boolean) =>
                            priceChangeDispatch({type: "USER_CONFIRMS_PRICE", payload: checked})}
                        testId="price-confirm-checkbox"
                    />
                </div>

                <SubmitBtn
                    text="Next"
                    breadcrumbObject={breadcrumbObject}
                    componentName={componentName}
                    disabled={!priceChangeState.priceConfirmed}
                />
            </form>
        </>
    );
}
